import { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../../components/Navbar";
import { BASE_URL } from "../../const";

// ── helpers ────────────────────────────────────────────────────────────────────
function authHeader() {
  const t = localStorage.getItem("token");
  if (!t) return {};
  return { Authorization: t.startsWith("Bearer ") || t.startsWith("Token ") ? t : `Token ${t}` };
}

const PARTY_TYPES = ["vendor", "warehouse", "customer", "other"];

const PARTY_TYPE_COLORS = {
  vendor:    { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  warehouse: { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  customer:  { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  other:     { bg: "#fafafa", text: "#525252", border: "#e5e5e5" },
};

const EMPTY_FORM = {
  party_name: "", party_code: "", party_type: "vendor", gstin: "", pan: "",
  contact_person: "", contact_email: "", contact_phone: "",
  billing_attention: "", billing_address: "", billing_street2: "",
  billing_city: "", billing_state: "", billing_pincode: "", billing_country: "India",
  same_as_billing: false,
  shipping_attention: "", shipping_address: "", shipping_street2: "",
  shipping_city: "", shipping_state: "", shipping_pincode: "", shipping_country: "India",
  is_active: true, remarks: "",
};

// ── CSV helpers ────────────────────────────────────────────────────────────────
const CSV_COLS = [
  "party_name","party_code","party_type","gstin","pan",
  "contact_person","contact_email","contact_phone",
  "billing_attention","billing_address","billing_street2",
  "billing_city","billing_state","billing_pincode","billing_country",
  "same_as_billing",
  "shipping_attention","shipping_address","shipping_street2",
  "shipping_city","shipping_state","shipping_pincode","shipping_country",
  "is_active","remarks",
];

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(rows) {
  const lines = [
    CSV_COLS.join(","),
    ...rows.map(r => CSV_COLS.map(c => csvEscape(r[c])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `parties_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadTemplate() {
  const blob = new Blob([CSV_COLS.join(",") + "\n"], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "parties_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map(line => {
    const values = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { values.push(cur); cur = ""; }
      else { cur += ch; }
    }
    values.push(cur);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (values[i] || "").trim(); });
    return obj;
  }).filter(r => r.party_name || r.party_code);
}

// ── ImportModal ────────────────────────────────────────────────────────────────
const PREVIEW_COLS = ["party_name", "party_code", "party_type", "billing_city", "billing_state"];

function ImportModal({ onClose, onImported }) {
  const [step,      setStep]      = useState("upload"); // upload | preview | importing | done
  const [rows,      setRows]      = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [results,   setResults]   = useState({ ok: 0, fail: 0, errors: [] });
  const fileRef = useRef(null);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCsv(ev.target.result);
      setRows(parsed);
      setStep(parsed.length ? "preview" : "upload");
      if (!parsed.length) alert("No valid rows found in the CSV. Make sure it matches the template.");
    };
    reader.readAsText(file);
  }

  async function handleImport() {
    setImporting(true);
    setStep("importing");
    let ok = 0, fail = 0;
    const errors = [];
    for (let i = 0; i < rows.length; i++) {
      const raw = rows[i];
      const payload = {
        ...EMPTY_FORM, ...raw,
        is_active:      raw.is_active      !== "false",
        same_as_billing: raw.same_as_billing === "true",
      };
      try {
        const res  = await fetch(`${BASE_URL}/vendor/parties/create/`, {
          method: "POST",
          headers: { ...authHeader(), "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || JSON.stringify(data?.errors || data));
        ok++;
      } catch (e) {
        fail++;
        errors.push(`Row ${i + 1} (${raw.party_name || "?"}): ${e.message}`);
      }
      setProgress(i + 1);
    }
    setResults({ ok, fail, errors });
    setStep("done");
    setImporting(false);
    if (ok > 0) onImported();
  }

  const pct = rows.length ? Math.round((progress / rows.length) * 100) : 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "min(680px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>Import Parties</h2>
            <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Bulk-create parties from a CSV file</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "20px 24px", flex: 1 }}>

          {/* ── upload step ── */}
          {step === "upload" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Step 1 — Download template</div>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Get the CSV with all column headers</div>
                </div>
                <button
                  onClick={downloadTemplate}
                  style={{ padding: "7px 14px", border: "1px solid #6366f1", borderRadius: 7, background: "#eef2ff", fontSize: 12, cursor: "pointer", color: "#6366f1", fontWeight: 600 }}
                >
                  ↓ Template
                </button>
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                style={{ border: "2px dashed #e2e8f0", borderRadius: 10, padding: "44px 24px", textAlign: "center", cursor: "pointer", background: "#fafafa", transition: "border-color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#6366f1"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}
              >
                <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Step 2 — Upload your CSV</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>Click here or drag & drop a .csv file</div>
                <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} />
              </div>
            </div>
          )}

          {/* ── preview step ── */}
          {step === "preview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  {rows.length} row{rows.length !== 1 ? "s" : ""} ready to import
                </div>
                <button
                  onClick={() => { setStep("upload"); setRows([]); if (fileRef.current) fileRef.current.value = ""; }}
                  style={{ fontSize: 12, color: "#6366f1", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                >
                  ← Change file
                </button>
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ overflowX: "auto", maxHeight: 280 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", position: "sticky", top: 0 }}>
                        <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#9ca3af", width: 36 }}>#</th>
                        {PREVIEW_COLS.map(c => (
                          <th key={c} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>
                            {c.replace(/_/g, " ")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f9fafb" }}>
                          <td style={{ padding: "7px 12px", color: "#9ca3af" }}>{i + 1}</td>
                          {PREVIEW_COLS.map(c => (
                            <td key={c} style={{ padding: "7px 12px", color: "#374151", whiteSpace: "nowrap" }}>{r[c] || "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>Preview shows key columns only — all fields from the CSV will be imported.</p>
            </div>
          )}

          {/* ── importing step ── */}
          {step === "importing" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18, padding: "20px 0" }}>
              <div style={{ fontSize: 36 }}>⏳</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Importing… {progress} / {rows.length}</div>
              <div style={{ width: "100%", height: 8, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: "#6366f1", borderRadius: 999, transition: "width 0.2s" }} />
              </div>
            </div>
          )}

          {/* ── done step ── */}
          {step === "done" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "10px 0" }}>
              <div style={{ fontSize: 42 }}>{results.fail === 0 ? "✅" : "⚠️"}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Import Complete</div>
              <div style={{ display: "flex", gap: 14 }}>
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "12px 28px", textAlign: "center" }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "#166534" }}>{results.ok}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", marginTop: 2 }}>CREATED</div>
                </div>
                <div style={{ background: results.fail ? "#fef2f2" : "#f8fafc", border: `1px solid ${results.fail ? "#fecaca" : "#e2e8f0"}`, borderRadius: 10, padding: "12px 28px", textAlign: "center" }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: results.fail ? "#dc2626" : "#9ca3af" }}>{results.fail}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: results.fail ? "#dc2626" : "#9ca3af", marginTop: 2 }}>FAILED</div>
                </div>
              </div>
              {results.errors.length > 0 && (
                <div style={{ width: "100%", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 14px", maxHeight: 160, overflowY: "auto" }}>
                  {results.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: 12, color: "#dc2626", marginBottom: 4 }}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>
            {step === "done" ? "Close" : "Cancel"}
          </button>
          {step === "preview" && (
            <button
              onClick={handleImport}
              style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#6366f1", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Import {rows.length} {rows.length === 1 ? "party" : "parties"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── sub-components ─────────────────────────────────────────────────────────────
function Badge({ type }) {
  const c = PARTY_TYPE_COLORS[type] || PARTY_TYPE_COLORS.other;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>
      {type}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span style={{ background: active ? "#f0fdf4" : "#fef2f2", color: active ? "#166534" : "#dc2626", border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
        {label}{required && <span style={{ color: "#ef4444" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8,
  fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box",
  background: "#fff",
};

const sectionHeadStyle = {
  fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase",
  letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6,
  borderBottom: "1px solid #e0e7ff",
};

// ── main component ─────────────────────────────────────────────────────────────
export default function PartyDetailsPage() {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pageSize]                = useState(50);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [filters, setFilters]     = useState({ search: "", party_type: "", is_active: "" });
  const [modalOpen,  setModalOpen]  = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editRow,    setEditRow]    = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (filters.search)     params.set("search",     filters.search);
      if (filters.party_type) params.set("party_type", filters.party_type);
      if (filters.is_active !== "") params.set("is_active", filters.is_active);
      const res  = await fetch(`${BASE_URL}/vendor/parties/?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load");
      setRows(data?.results || data?.data?.results || data?.data || []);
      setTotal(data?.count || data?.data?.count || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openCreate() {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditRow(row);
    setForm({ ...EMPTY_FORM, ...row });
    setModalOpen(true);
  }

  function handleDuplicate(row) {
    setEditRow(null);
    setForm({
      ...EMPTY_FORM, ...row,
      party_name: `${row.party_name} (Copy)`,
      party_code: `${row.party_code}-COPY`,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const url    = editRow ? `${BASE_URL}/vendor/parties/${editRow.id}/` : `${BASE_URL}/vendor/parties/create/`;
      const method = editRow ? "PUT" : "POST";
      const res    = await fetch(url, {
        method, headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Save failed");
      setModalOpen(false);
      fetchData();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      const res = await fetch(`${BASE_URL}/vendor/parties/${id}/`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error("Delete failed");
      setDeleteId(null);
      fetchData();
    } catch (e) {
      alert(e.message);
    } finally {
      setDeleting(false);
    }
  }

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <Navbar />

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 20px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Party Details</h1>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Manage vendors, warehouses & parties</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => exportCsv(rows)}
              disabled={rows.length === 0}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#374151", fontWeight: 600, fontSize: 13, cursor: rows.length === 0 ? "default" : "pointer", opacity: rows.length === 0 ? 0.5 : 1 }}
            >
              ↓ Export CSV
            </button>
            <button
              onClick={() => setImportOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 8, color: "#6366f1", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              ↑ Import CSV
            </button>
            <button onClick={openCreate} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#ea580c", border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              + Add Party
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            placeholder="Search name, code, GSTIN…"
            value={filters.search}
            onChange={e => { setFilters(p => ({ ...p, search: e.target.value })); setPage(1); }}
            style={{ ...inputStyle, width: 240 }}
          />
          <select value={filters.party_type} onChange={e => { setFilters(p => ({ ...p, party_type: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 160 }}>
            <option value="">All Types</option>
            {PARTY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <select value={filters.is_active} onChange={e => { setFilters(p => ({ ...p, is_active: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 140 }}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button onClick={() => { setFilters({ search: "", party_type: "", is_active: "" }); setPage(1); }} style={{ padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 12, color: "#6b7280", cursor: "pointer" }}>
            Clear
          </button>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af" }}>{total} records</span>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  {["Code", "Name", "Type", "GSTIN", "Contact Person", "Phone", "City", "State", "Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap", fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>No parties found</td></tr>
                ) : rows.map((row, i) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafafa" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafafa"}
                  >
                    <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>{row.party_code}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 500, color: "#111827" }}>{row.party_name}</td>
                    <td style={{ padding: "10px 14px" }}><Badge type={row.party_type} /></td>
                    <td style={{ padding: "10px 14px", color: "#6b7280", fontFamily: "monospace", fontSize: 12 }}>{row.gstin || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#374151" }}>{row.contact_person || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>{row.contact_phone || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>{row.billing_city || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>{row.billing_state || "—"}</td>
                    <td style={{ padding: "10px 14px" }}><ActiveBadge active={row.is_active} /></td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button onClick={() => openEdit(row)} style={{ padding: "4px 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", fontSize: 12, cursor: "pointer", color: "#374151" }}>Edit</button>
                        <button
                          onClick={() => handleDuplicate(row)}
                          title="Duplicate this party"
                          style={{ padding: "4px 10px", border: "1px solid #c7d2fe", borderRadius: 6, background: "#eef2ff", fontSize: 12, cursor: "pointer", color: "#6366f1" }}
                        >
                          Copy
                        </button>
                        <button onClick={() => setDeleteId(row.id)} style={{ padding: "4px 10px", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", fontSize: 12, cursor: "pointer", color: "#dc2626" }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Page {page} of {totalPages}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 6, background: page <= 1 ? "#f8fafc" : "#fff", cursor: page <= 1 ? "default" : "pointer", fontSize: 12, color: "#374151" }}>← Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 6, background: page >= totalPages ? "#f8fafc" : "#fff", cursor: page >= totalPages ? "default" : "pointer", fontSize: 12, color: "#374151" }}>Next →</button>
            </div>
          </div>
        </div>
      </div>

      {/* Import modal */}
      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onImported={fetchData}
        />
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 340, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Delete Party?</h3>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>This action cannot be undone.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteId(null)} style={{ padding: "8px 16px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleDelete(deleteId)} disabled={deleting} style={{ padding: "8px 16px", border: "none", borderRadius: 8, background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{deleting ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
          <div style={{ background: "#fff", width: "min(640px, 100vw)", height: "100vh", overflowY: "auto", boxShadow: "-8px 0 40px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column" }}>
            {/* Modal header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>{editRow ? "Edit Party" : "Add Party"}</h2>
                {editRow && <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", marginTop: 2 }}>ID: {editRow.id}</p>}
              </div>
              <button onClick={() => setModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 20, height: 20, color: "#6b7280" }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Form body */}
            <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Identity */}
              <div>
                <p style={sectionHeadStyle}>Identity</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FormField label="Party Name" required><input style={inputStyle} value={form.party_name} onChange={e => f("party_name", e.target.value)} /></FormField>
                  <FormField label="Party Code" required><input style={inputStyle} value={form.party_code} onChange={e => f("party_code", e.target.value)} placeholder="e.g. VND-001" /></FormField>
                  <FormField label="Party Type">
                    <select style={inputStyle} value={form.party_type} onChange={e => f("party_type", e.target.value)}>
                      {PARTY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Active">
                    <select style={inputStyle} value={form.is_active ? "true" : "false"} onChange={e => f("is_active", e.target.value === "true")}>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </FormField>
                  <FormField label="GSTIN"><input style={inputStyle} value={form.gstin} onChange={e => f("gstin", e.target.value)} /></FormField>
                  <FormField label="PAN"><input style={inputStyle} value={form.pan} onChange={e => f("pan", e.target.value)} /></FormField>
                </div>
              </div>

              {/* Contact */}
              <div>
                <p style={sectionHeadStyle}>Contact</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FormField label="Contact Person"><input style={inputStyle} value={form.contact_person} onChange={e => f("contact_person", e.target.value)} /></FormField>
                  <FormField label="Phone"><input style={inputStyle} value={form.contact_phone} onChange={e => f("contact_phone", e.target.value)} /></FormField>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <FormField label="Email"><input style={inputStyle} type="email" value={form.contact_email} onChange={e => f("contact_email", e.target.value)} /></FormField>
                  </div>
                </div>
              </div>

              {/* Billing */}
              <div>
                <p style={sectionHeadStyle}>Billing Address</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ gridColumn: "1 / -1" }}><FormField label="Attention"><input style={inputStyle} value={form.billing_attention} onChange={e => f("billing_attention", e.target.value)} /></FormField></div>
                  <div style={{ gridColumn: "1 / -1" }}><FormField label="Address Line 1"><input style={inputStyle} value={form.billing_address} onChange={e => f("billing_address", e.target.value)} /></FormField></div>
                  <div style={{ gridColumn: "1 / -1" }}><FormField label="Address Line 2"><input style={inputStyle} value={form.billing_street2} onChange={e => f("billing_street2", e.target.value)} /></FormField></div>
                  <FormField label="City"><input style={inputStyle} value={form.billing_city} onChange={e => f("billing_city", e.target.value)} /></FormField>
                  <FormField label="State"><input style={inputStyle} value={form.billing_state} onChange={e => f("billing_state", e.target.value)} /></FormField>
                  <FormField label="Pincode"><input style={inputStyle} value={form.billing_pincode} onChange={e => f("billing_pincode", e.target.value)} /></FormField>
                  <FormField label="Country"><input style={inputStyle} value={form.billing_country} onChange={e => f("billing_country", e.target.value)} /></FormField>
                </div>
              </div>

              {/* Shipping */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e0e7ff" }}>
                  <p style={{ ...sectionHeadStyle, marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>Shipping Address</p>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280", cursor: "pointer" }}>
                    <input type="checkbox" checked={form.same_as_billing} onChange={e => f("same_as_billing", e.target.checked)} />
                    Same as billing
                  </label>
                </div>
                {!form.same_as_billing && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ gridColumn: "1 / -1" }}><FormField label="Attention"><input style={inputStyle} value={form.shipping_attention} onChange={e => f("shipping_attention", e.target.value)} /></FormField></div>
                    <div style={{ gridColumn: "1 / -1" }}><FormField label="Address Line 1"><input style={inputStyle} value={form.shipping_address} onChange={e => f("shipping_address", e.target.value)} /></FormField></div>
                    <div style={{ gridColumn: "1 / -1" }}><FormField label="Address Line 2"><input style={inputStyle} value={form.shipping_street2} onChange={e => f("shipping_street2", e.target.value)} /></FormField></div>
                    <FormField label="City"><input style={inputStyle} value={form.shipping_city} onChange={e => f("shipping_city", e.target.value)} /></FormField>
                    <FormField label="State"><input style={inputStyle} value={form.shipping_state} onChange={e => f("shipping_state", e.target.value)} /></FormField>
                    <FormField label="Pincode"><input style={inputStyle} value={form.shipping_pincode} onChange={e => f("shipping_pincode", e.target.value)} /></FormField>
                    <FormField label="Country"><input style={inputStyle} value={form.shipping_country} onChange={e => f("shipping_country", e.target.value)} /></FormField>
                  </div>
                )}
              </div>

              {/* Remarks */}
              <FormField label="Remarks">
                <textarea style={{ ...inputStyle, height: 72, resize: "vertical" }} value={form.remarks} onChange={e => f("remarks", e.target.value)} />
              </FormField>
            </div>

            {/* Footer */}
            <div style={{ padding: "14px 24px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 10, justifyContent: "flex-end", position: "sticky", bottom: 0, background: "#fff" }}>
              <button onClick={() => setModalOpen(false)} style={{ padding: "8px 18px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#ea580c", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {saving ? "Saving…" : editRow ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
